import { Tooltip, useTheme } from '@flik/shared';
import React, { useEffect, useRef, useState } from 'react';

interface KebabMenuAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  tooltip?: string;
  destructive?: boolean;
  className?: string;
}

interface KebabMenuProps {
  actions: KebabMenuAction[];
  className?: string;
}

const KebabIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="19" r="1" />
  </svg>
);

const KebabMenu: React.FC<KebabMenuProps> = ({ actions, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { theme } = useTheme();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleActionClick = (action: KebabMenuAction, e: React.MouseEvent) => {
    e.stopPropagation();
    action.onClick();
    setIsOpen(false);
  };

  if (actions.length === 0) {
    return null;
  }

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      {/* Kebab Button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`p-1 rounded transition-colors ${
          theme === 'light'
            ? 'text-gray-500 hover:bg-gray-200'
            : 'text-gray-400 hover:bg-gray-600'
        }`}
        title="More options"
      >
        <KebabIcon />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute left-0 top-0 w-44 rounded-md shadow-lg z-50 transform -translate-x-full -translate-y-1 -ml-1 mt-1 animate-in fade-in-0 slide-in-from-top-2 duration-200 ${
            theme === 'light'
              ? 'bg-white border border-gray-200'
              : 'bg-black border border-gray-700'
          }`}
        >
          {actions.map((action) => (
            <Tooltip
              key={action.id}
              content={action.tooltip || action.label}
              direction="left"
            >
              <button
                onClick={(e) => handleActionClick(action, e)}
                className={`w-full px-2 py-1.5 text-left transition-colors cursor-pointer flex items-center space-x-1.5 text-xs rounded-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                  action.destructive
                    ? 'text-red-400 hover:bg-red-900/20'
                    : theme === 'light'
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-white hover:bg-gray-800'
                } ${action.className || ''}`}
              >
                {action.icon && (
                  <span className="flex-shrink-0">{action.icon}</span>
                )}
                <span className="font-medium">{action.label}</span>
              </button>
            </Tooltip>
          ))}
        </div>
      )}
    </div>
  );
};

export default KebabMenu;
